import { type FC, useCallback } from 'react'

import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/table/table'

import type {
  ApplicantsSort,
  ApplicantsSortKey,
  ApplicantsSortOrder,
  ResApplicant,
} from '@/lib/queries/business/v2/applicant/search/types'
import { cn } from '@/lib/utils'

import { SortButton } from '../../../../ui/table/table-sort-button/table-sort-button'
import { ApplicantRow } from './applicant-row/applicant-row'
import { SelectButton } from './select-button/select-button'

interface Props {
  className?: string
  disabled?: boolean
  applicants: ResApplicant[]
  defaultSort: ApplicantsSort
  sort: ApplicantsSort
  onSortChange: (sort: ApplicantsSort) => Promise<void>
  invalidate: () => Promise<void>
  selectedIds: number[]
  isEveryRowSelected: boolean
  onSelectionToggle: (applicantId: number) => void
  onAllSelectionToggle: () => void
}

export const ApplicantsTable: FC<Props> = ({
  className,
  disabled = false,
  applicants,
  invalidate,
  defaultSort,
  sort,
  onSortChange,
  selectedIds,
  isEveryRowSelected,
  onSelectionToggle,
  onAllSelectionToggle,
}) => {
  const sortOrderChangeHandler = useCallback(
    (key: ApplicantsSortKey) => {
      return async (order?: ApplicantsSortOrder) => {
        if (!order) {
          await onSortChange(defaultSort)
        } else {
          const newSort = { key, order }
          await onSortChange(newSort)
        }
      }
    },
    [defaultSort, onSortChange]
  )

  return (
    <div className={cn('rounded-md border', className)}>
      <Table className="text-sm">
        <TableHeader>
          <TableRow className={cn('text-xs font-medium [&>th]:h-10 [&>th]:py-0', 'hover:bg-transparent')}>
            <TableHead className="w-10 px-0">
              <SelectButton disabled={disabled} checked={isEveryRowSelected} onClick={onAllSelectionToggle} />
            </TableHead>
            <TableHead>
              <SortButton
                title="Name"
                sortOrder={sort.key === 'name' ? sort.order : undefined}
                disabled={disabled}
                onSortOrderChange={sortOrderChangeHandler('name')}
              />
            </TableHead>
            <TableHead>
              <SortButton
                title="Email"
                sortOrder={sort.key === 'email' ? sort.order : undefined}
                disabled={disabled}
                onSortOrderChange={sortOrderChangeHandler('email')}
              />
            </TableHead>
            <TableHead>Phone Number</TableHead>
            <TableHead>Tags</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>

        <TableBody>
          {applicants.map((applicant) => {
            return (
              <ApplicantRow
                key={applicant.applicant_id}
                selected={selectedIds.includes(applicant.path_id)}
                applicant={applicant}
                invalidate={invalidate}
                onSelectionToggle={onSelectionToggle}
              />
            )
          })}
        </TableBody>
      </Table>

      {applicants.length === 0 && (
        <div className={cn('flex h-[570px] items-center justify-center', 'text-center text-gray-500')}>
          No applicants yet.
        </div>
      )}
    </div>
  )
}
